import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import type { PurchaseLotHistoryEntry } from '@/lib/api/purchase-lot-api'
import { HistoryEntryCard } from '@/components/common/HistoryEntryCard'
import {
    PurchaseLotStatusBadge,
    purchaseLotInReasonLabel,
    purchaseLotOutReasonLabel,
} from '@/components/purchase-lots/purchase-lot-badges'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    usePurchaseLot,
    usePurchaseLotHistory,
} from '@/hooks/use-purchase-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/lots/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data: lotRes, isLoading } = usePurchaseLot(id)
    const historyQuery = usePurchaseLotHistory(id)
    const lot = lotRes?.data
    const history = historyQuery.data?.pages.flatMap((p) => p.data ?? []) ?? []

    if (isLoading) return <div className="p-6">{m.pl_lots_loading()}</div>
    if (!lot)
        return <div className="p-6">{m.product_lot_detail_not_found()}</div>

    return (
        <div className="space-y-4 p-4 md:p-6">
            <Button
                asChild
                variant="ghost"
                className="h-11 md:h-9 -ml-2 md:ml-0"
            >
                <Link to="/purchase-lots/lots" search={{}}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.sl_lot_back()}
                </Link>
            </Button>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base md:text-2xl">
                        {m.pl_lot_detail_title()}{' '}
                        <span className="font-mono">{lot.code}</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <Field
                        label={m.pl_lots_col_purchase()}
                        value={lot.purchase_code ?? '—'}
                    />
                    <Field
                        label={m.pl_lots_col_grower()}
                        value={lot.grower_name ?? '—'}
                    />
                    <Field
                        label={m.pl_lots_col_available()}
                        value={`${formatQty(lot.quantity_available)} kg`}
                    />
                    <Field
                        label={m.sl_lots_col_created_at()}
                        value={formatDateTimeVN(lot.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={lot.created_by_name ?? '—'}
                    />
                </CardContent>
            </Card>

            <section className="space-y-2">
                <h2 className="text-base font-semibold">
                    {m.pl_lot_history_title()}
                </h2>
                {history.length === 0 ? (
                    <div className="rounded-md border bg-card py-8 text-center text-muted-foreground">
                        {m.pl_history_empty()}
                    </div>
                ) : (
                    <>
                        {/* Mobile: card list */}
                        <div className="space-y-2 md:hidden">
                            {history.map((row) => (
                                <HistoryEntryCard
                                    key={`${row.note_type}-${row.note_id}`}
                                    type={row.note_type}
                                    createdAt={row.created_at}
                                    noteLink={<NoteLink entry={row} />}
                                    statusBadge={
                                        <PurchaseLotStatusBadge
                                            status={row.status}
                                        />
                                    }
                                    reason={
                                        row.note_type === 'in'
                                            ? purchaseLotInReasonLabel(
                                                  row.reason,
                                              )
                                            : purchaseLotOutReasonLabel(
                                                  row.reason,
                                              )
                                    }
                                    notes={row.notes}
                                    quantity={row.quantity}
                                    unit="kg"
                                    createdBy={row.created_by_name}
                                />
                            ))}
                        </div>

                        {/* Desktop: table */}
                        <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>
                                            {m.pl_history_col_note()}
                                        </TableHead>
                                        <TableHead>
                                            {m.pl_history_col_type()}
                                        </TableHead>
                                        <TableHead>
                                            {m.pl_history_col_reason()}
                                        </TableHead>
                                        <TableHead>
                                            {m.pl_history_col_status()}
                                        </TableHead>
                                        <TableHead className="text-right">
                                            {m.pl_history_col_quantity()}
                                        </TableHead>
                                        <TableHead>
                                            {m.pl_history_col_created_at()}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {history.map((row) => (
                                        <TableRow
                                            key={`${row.note_type}-${row.note_id}`}
                                        >
                                            <TableCell className="font-mono text-xs">
                                                {row.note_code}
                                            </TableCell>
                                            <TableCell>
                                                {row.note_type === 'in'
                                                    ? m.pl_note_type_in()
                                                    : m.pl_note_type_out()}
                                            </TableCell>
                                            <TableCell>
                                                {row.note_type === 'in'
                                                    ? purchaseLotInReasonLabel(
                                                          row.reason,
                                                      )
                                                    : purchaseLotOutReasonLabel(
                                                          row.reason,
                                                      )}
                                            </TableCell>
                                            <TableCell>
                                                <PurchaseLotStatusBadge
                                                    status={row.status}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {row.note_type === 'in'
                                                    ? '+'
                                                    : '-'}
                                                {formatQty(row.quantity)} kg
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatDateTimeVN(
                                                    row.created_at,
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
                {historyQuery.hasNextPage && (
                    <Button
                        variant="outline"
                        className="w-full h-11 mt-3"
                        disabled={historyQuery.isFetchingNextPage}
                        onClick={() => void historyQuery.fetchNextPage()}
                    >
                        {historyQuery.isFetchingNextPage
                            ? m.list_loading_more()
                            : m.list_load_more()}
                    </Button>
                )}
            </section>
        </div>
    )
}

function NoteLink({ entry }: { entry: PurchaseLotHistoryEntry }) {
    if (entry.note_type === 'in') {
        return (
            <Link
                to="/purchase-lots/stock-in-notes/$id"
                params={{ id: entry.note_id }}
                className="font-mono text-sm text-primary hover:underline"
            >
                {entry.note_code}
            </Link>
        )
    }
    return (
        <Link
            to="/purchase-lots/stock-out-notes/$id"
            params={{ id: entry.note_id }}
            className="font-mono text-sm text-primary hover:underline"
        >
            {entry.note_code}
        </Link>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
